import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import Spinner from '@amzn/awsui-components-console/spinner';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import { CognitoSession, cognitoLogout, getCognitoGroups, getCognitoSession, startExternalLogin } from '../auth/cognitoAuth';
import { ExternalAuthConfig } from '../config/externalAuthConfig';

interface CognitoContextType {
  idToken: string;
  userName: string | null;
  userEmail: string | null;
  // Cognito groups, e.g. reviewer.
  groups: string[];
  signOut: () => void;
}

const CognitoContext = createContext<CognitoContextType | undefined>(undefined);

interface CognitoProviderProps {
  config: ExternalAuthConfig;
  children: ReactNode;
}

const toContext = (session: CognitoSession, config: ExternalAuthConfig): CognitoContextType => {
  const { claims } = session;
  const fullName = [claims.given_name, claims.family_name].filter(Boolean).join(' ');
  return {
    idToken: session.idToken,
    userName: fullName || null,
    userEmail: claims.email ?? null,
    groups: getCognitoGroups(session),
    signOut: () => cognitoLogout(config),
  };
};

/** Requires a Cognito Hosted UI session; redirects to the Hosted UI when there is none. */
export const CognitoProvider: React.FC<CognitoProviderProps> = ({ config, children }) => {
  const [session] = useState<CognitoSession | null>(() => getCognitoSession());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session) {
      return;
    }
    startExternalLogin(config).catch((err) => {
      console.error('Failed to start Cognito login:', err);
      setError('Unable to start sign-in. Please try again later.');
    });
  }, [session, config]);

  if (error) {
    return (
      <Box padding="xxl">
        <Alert type="error" header="Sign-in unavailable">
          {error}
        </Alert>
      </Box>
    );
  }

  if (!session) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spinner size="large" />
      </div>
    );
  }

  return <CognitoContext.Provider value={toContext(session, config)}>{children}</CognitoContext.Provider>;
};

/** Cognito session for the Media Processing pages, or undefined outside a CognitoProvider. */
export const useOptionalCognito = (): CognitoContextType | undefined => useContext(CognitoContext);
